/**
 * Page-level customer / group context pickers for context-first grids.
 *
 * Some staff work queues (RMAs, sales orders) are context-first: pick the
 * customer, then one of their groups, and that context frames the whole
 * page — the grid and stat cards filter to it, and the typed scope search
 * writes the same state, so a picked suggestion fills these pickers. The
 * group picker stays disabled until a customer is chosen, which is also
 * what scopes its options.
 *
 * Composes the shared CustomerAutocomplete / SiteGroupAutocomplete, so the
 * pickers get their lazy server-side search, match highlighting, and
 * initial-selection label hydration. State lives in the consuming grid.
 *
 * Contains:
 * - ScopeContextPickers — the picker row (default export)
 */
import { Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';

const PICKER_WIDTH = 240;

// The autocompletes match options by identity (`option.value === value`), and
// option values are numeric ids — persisted grid state may hold strings.
const toPickerValue = id => (id != null && id !== '' ? Number(id) : '');

function ScopeContextPickers({
  customerTerm,
  siteGroupTerm,
  customerId,
  siteGroupId,
  onCustomerChange,
  onSiteGroupChange,
}) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ mb: 'var(--page-gap)', flexWrap: 'wrap', rowGap: 1 }}>
      {/* fullWidth stretches the inner TextField to the sized root — without
          it the input collapses to its intrinsic width. */}
      <CustomerAutocomplete
        name="scope-customer"
        size="small"
        fullWidth
        label={customerTerm}
        value={toPickerValue(customerId)}
        onChange={(event, id, option) => onCustomerChange(option ? { id: option.value, label: option.label } : null)}
        sx={{ width: PICKER_WIDTH }}
      />
      <SiteGroupAutocomplete
        name="scope-site-group"
        size="small"
        fullWidth
        label={siteGroupTerm}
        customerId={toPickerValue(customerId) || null}
        disabled={!customerId}
        value={toPickerValue(siteGroupId)}
        onChange={(event, id, option) => onSiteGroupChange(option ? { id: option.value, label: option.label } : null)}
        sx={{ width: PICKER_WIDTH }}
      />
    </Stack>
  );
}

ScopeContextPickers.propTypes = {
  customerTerm: PropTypes.string.isRequired,
  siteGroupTerm: PropTypes.string.isRequired,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCustomerChange: PropTypes.func.isRequired,
  onSiteGroupChange: PropTypes.func.isRequired,
};

export default ScopeContextPickers;
